import React from 'react';
import classNames from 'classnames';
import { t } from 'i18next';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import ArrowDropUp from '@material-ui/icons/ArrowDropUp';
import MoreVerticalIcon from '@material-ui/icons/MoreVert';
import DoneIcon from '@material-ui/icons/Done';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import {
  committalTypes,
  serviceCategories,
  productCategories,
  emptyCategoryInformation,
} from 'constants/arrangement';
import Price from 'components/common/Price';
import styles from 'scss/main.scss';
import { buildAddressString } from 'services/utils';
import { arrangementType } from 'types/bereavement';

import rcStyles from './styles.scss';

const ArrangementListItem = ({
  item,
  anchorElement,
  disabled,
  isExpanded,
  viewArrangement,
  confirmArrangement,
  unconfirmArrangement,
  onMenuOpen,
  onMenuClose,
  onEditReference,
  toggleArrangementCollapse,
}) => {
  const { committalType } = item;
  const isCremation = committalType === committalTypes.CREMATION;
  const categoryInformation = item.categoryInformation || emptyCategoryInformation;
  const serviceSelections = item.serviceSelections || [];
  const productSelections = item.productSelections || [];

  const serviceVenueSelection = serviceSelections
    .find(({ service }) => service.category === serviceCategories.SERVICE_VENUES);
  const crematoriumSelection = serviceSelections
    .find(({ service }) => service.category === serviceCategories.CREMATORIA);
  const cemeterySelection = serviceSelections
    .find(({ service }) => service.category === serviceCategories.CEMETERIES);
  const officiantSelection = serviceSelections
    .find(({ service }) => service.category === serviceCategories.OFFICIANTS);
  const coffinSelection = productSelections
    .find(({ product }) => product.category === productCategories.COFFINS);
  const urnSelection = productSelections
    .find(({ product }) => product.category === productCategories.URNS);
  const flowersSelections = productSelections
    .filter(({ product }) => product.category === productCategories.FLOWERS);
  const vehiclesSelections = serviceSelections
    .filter(({ service }) => service.category === serviceCategories.VEHICLES);

  return (
    <li
      key={item.id}
      data-test-id="arrangement"
      className={rcStyles.arrangement_item}
    >
      <ButtonBase
        classes={{ root: rcStyles.expanding_button }}
        onClick={toggleArrangementCollapse}
        data-test-id="arrangementExpandToggle"
      >
        <div className={rcStyles.banner_content}>
          <div className={classNames(
            rcStyles.banner_content__primary,
            item.isConfirmed ? rcStyles.is_confirmed : rcStyles.is_unconfirmed,
          )}
          >
            {isExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
            <div className={rcStyles.is_labels}>
              <h3 className={rcStyles.is_title}>{item.committalType}</h3>
              <p className={styles.brevier}>{item.isConfirmed ? 'Confirmed' : 'Unconfirmed'}</p>
            </div>
          </div>
          <div className={rcStyles.is_reference}>
            <div>{t('Reference')}</div>
            <div>{item.reference}</div>
          </div>
        </div>
      </ButtonBase>
      <div
        data-test-id="arrangementPrice"
        className={rcStyles.arrangement_item__price}
      >
        <Link
          to={`arrangement/${item.id}/estimate`}
        >
          <div className={rcStyles.is_primary}>
            <Price price={item.calculation.finalTotal} />
          </div>
        </Link>
      </div>
      <div className={rcStyles.arrangement_item__action}>
        <IconButton
          aria-controls="arrangement-more"
          aria-haspopup="true"
          data-test-id="arrangementMore"
          onClick={onMenuOpen}
        >
          <MoreVerticalIcon />
        </IconButton>
        <Menu
          anchorEl={anchorElement}
          open={!!anchorElement}
          onClose={onMenuClose}
        >
          <MenuItem
            data-test-id="editReference"
            onClick={() => { onMenuClose(); onEditReference(item); }}
          >
            {t('Edit reference')}
          </MenuItem>
        </Menu>
      </div>
      <div className={rcStyles.expanded_content}>
        <Collapse in={isExpanded}>
          <div className={rcStyles.arrangement__grid}>
            <Link
              to={`arrangement/${item.id}/service-venue`}
              className={rcStyles.arrangement__grid__item}
            >
              {categoryInformation.serviceVenue && categoryInformation.serviceVenue.isConfirmed && (
                <DoneIcon className={rcStyles.is_icon} />
              )}
              <h3 className={rcStyles.is_label}>
                {t('Service')}
              </h3>
              <span>{serviceVenueSelection ? serviceVenueSelection.service.title : t('Not selected')}</span>
              <span>
                {serviceVenueSelection
                  && serviceVenueSelection.service.address
                  && buildAddressString(serviceVenueSelection.service.address)}
              </span>
            </Link>
            {committalType === committalTypes.CREMATION && (
              <Link
                to={`arrangement/${item.id}/crematorium`}
                className={rcStyles.arrangement__grid__item}
              >
                {categoryInformation.crematorium && categoryInformation.crematorium.isConfirmed && (
                  <DoneIcon className={rcStyles.is_icon} />
                )}
                <h3 className={rcStyles.is_label}>
                  {t('Cremation')}
                </h3>
                <span>{crematoriumSelection ? crematoriumSelection.service.title : t('Not selected')}</span>
                <span>
                  {crematoriumSelection
                    && crematoriumSelection.service.address
                    && buildAddressString(crematoriumSelection.service.address)}
                </span>
              </Link>
            )}
            <Link
              to={`arrangement/${item.id}/burial`}
              className={rcStyles.arrangement__grid__item}
            >
              {categoryInformation.cemetery && categoryInformation.cemetery.isConfirmed && (
                <DoneIcon className={rcStyles.is_icon} />
              )}
              <h3 className={rcStyles.is_label}>
                {t('Burial')}
              </h3>
              <span>{cemeterySelection ? cemeterySelection.service.title : t('Not selected')}</span>
              <span>
                {cemeterySelection
                  && cemeterySelection.service.address
                  && buildAddressString(cemeterySelection.service.address)}
              </span>
            </Link>
            <Link
              to={`arrangement/${item.id}/cortege`}
              className={rcStyles.arrangement__grid__item}
            >
              {categoryInformation.vehicles && categoryInformation.vehicles.isConfirmed && (
                <DoneIcon className={rcStyles.is_icon} />
              )}
              <h3 className={rcStyles.is_label}>
                {t('Cortege')}
              </h3>
              {
                vehiclesSelections.length > 0
                  ? vehiclesSelections.map((selection, index) => (
                    <span key={selection.id}>
                      {`${selection.service.title}`}
                      {vehiclesSelections.length > 1 && index !== vehiclesSelections.length - 1 && ', '}
                    </span>
                  ))
                  : t('Not selected')
              }
            </Link>
            <Link
              to={`arrangement/${item.id}/coffins`}
              className={rcStyles.arrangement__grid__item}
            >
              {categoryInformation.coffin && categoryInformation.coffin.isConfirmed && (
                <DoneIcon className={rcStyles.is_icon} />
              )}
              <h3 className={rcStyles.is_label}>
                {t('Coffin')}
              </h3>
              <span>{coffinSelection ? coffinSelection.product.title : t('Not selected')}</span>
            </Link>
            {isCremation && (
              <Link
                to={`arrangement/${item.id}/urns`}
                className={rcStyles.arrangement__grid__item}
              >
                {categoryInformation.urn && categoryInformation.urn.isConfirmed && (
                  <DoneIcon className={rcStyles.is_icon} />
                )}
                <h3 className={rcStyles.is_label}>
                  {t('Urn')}
                </h3>
                <span>{urnSelection ? urnSelection.product.title : t('Not selected')}</span>
              </Link>
            )}
            <Link
              to={`arrangement/${item.id}/officiants`}
              className={rcStyles.arrangement__grid__item}
            >
              {categoryInformation.officiant && categoryInformation.officiant.isConfirmed && (
                <DoneIcon className={rcStyles.is_icon} />
              )}
              <h3 className={rcStyles.is_label}>
                {t('Officiant')}
              </h3>
              <span>{officiantSelection ? officiantSelection.service.title : t('Not selected')}</span>
            </Link>
            <Link
              to={`arrangement/${item.id}/flowers`}
              className={rcStyles.arrangement__grid__item}
            >
              {categoryInformation.flowers && categoryInformation.flowers.isConfirmed && (
                <DoneIcon className={rcStyles.is_icon} />
              )}
              <h3 className={rcStyles.is_label}>
                {t('Flowers')}
              </h3>
              {
                flowersSelections.length > 0
                  ? flowersSelections.map(flowers => (
                    <span key={flowers.id}>{`${flowers.product.title} hearse, `}</span>
                  ))
                  : t('Not selected')
              }
            </Link>
            <Link
              to={`arrangement/${item.id}/estimate`}
              className={rcStyles.arrangement__grid__item}
            >
              <h3 className={rcStyles.is_label}>
                {t('Estimate Total')}
              </h3>
              <div
                className={rcStyles.is_estimate_price}
              >
                <Price price={item.calculation.finalTotal} />
              </div>
            </Link>
          </div>
          <div className={classNames(styles.c_k_tile__action_bar, styles.u_push__right, styles.u_push__bottom)}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => (
                item.isConfirmed
                  ? unconfirmArrangement(item.id)
                  : confirmArrangement(item.id)
              )}
              data-test-id="confirmation"
              disabled={disabled}
            >
              <span>{item.isConfirmed ? t('UNCONFIRM') : t('CONFIRM')}</span>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => viewArrangement(item.id)}
              data-test-id="viewAndEdit"
            >
              {disabled ? t('View') : t('View & Edit')}
            </Button>
          </div>
        </Collapse>
      </div>
    </li>
  );
};

ArrangementListItem.propTypes = {
  item: arrangementType.isRequired,
  anchorElement: PropTypes.objectOf(PropTypes.any),
  disabled: PropTypes.bool,
  isExpanded: PropTypes.bool.isRequired,
  viewArrangement: PropTypes.func.isRequired,
  confirmArrangement: PropTypes.func.isRequired,
  unconfirmArrangement: PropTypes.func.isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  onMenuClose: PropTypes.func.isRequired,
  onEditReference: PropTypes.func.isRequired,
  toggleArrangementCollapse: PropTypes.func.isRequired,
};

export default ArrangementListItem;
